/**
 * Retry policy for a chat turn whose stream never delivered a reply.
 *
 * The one failure a deploy still produces after the server-side drain
 * (#537) is the request that lands in the gap: the old container has
 * stopped listening, the router answers 502/503 for a beat, or the
 * socket resets before a byte arrives. Those turns retry once, after a
 * short pause, with `retry: true` so the server can skip re-storing the
 * visitor's message. A turn that already received events is NOT
 * retried — the reply was partly painted and the server has persisted
 * it; the visitor sees the error bubble and can ask again.
 */

export const CHAT_RETRY_DELAY_MS = 1_500;

/** Thrown by streamChat for a non-2xx response, carrying the status. */
export class ChatRequestError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`[showpilot] chat request failed: ${status}`);
    this.name = 'ChatRequestError';
    this.status = status;
  }
}

const RETRYABLE_STATUS = new Set([0, 408, 425, 429, 500, 502, 503, 504]);

export function shouldRetryTurn(input: {
  /** 0 on the first send; the policy allows exactly one retry. */
  attempt: number;
  /** Events already delivered to the UI on this attempt. */
  eventsSeen: number;
  err: unknown;
  /** The visitor navigated / cancelled — never retry an aborted turn. */
  aborted?: boolean;
}): boolean {
  if (input.attempt > 0) return false;
  if (input.eventsSeen > 0) return false;
  if (input.aborted) return false;
  const err = input.err;
  if (err instanceof ChatRequestError) return RETRYABLE_STATUS.has(err.status);
  // fetch() rejects with a TypeError on connection reset / refused / DNS.
  if (err instanceof TypeError) return true;
  if (err instanceof Error && err.name === 'AbortError') return false;
  return false;
}
